import React from 'react';
import { Shield, CheckCircle2 } from 'lucide-react';

/**
 * Centraliza en un solo lugar los 6 tipos de bloqueo que hoy comunicamos a las psicólogas
 * (ver /profile → "Tu estadía en Psi Mammoliti" → "Bloqueos").
 *
 * Fuentes de estado reusadas — NO se duplica lógica de detección, se reciben ya calculadas
 * desde PsychologistProfile.tsx (mismo origen que el banner "Bloqueada para recibir pacientes
 * nuevos" que ya existe en la pestaña "Información general"):
 *  - `activeBlockade` / `processLimitPrefered`: bloqueo de calendario activo (Tope, Sin
 *    horarios de reprogramación, Situación personal) — idéntico a `activeBlockade` en
 *    PsychologistProfile.tsx.
 *  - `activeVacationEnd`: bloqueo por vacaciones/días off — idéntico al usado por el banner de
 *    vacaciones en PsychologistProfile.tsx.
 *  - `blockades`: lista cruda de bloqueos (misma que ya usa MetricsAsignacionTab) — la única
 *    fuente para "Demasiados pacientes" / "Límite de pacientes dentro de un período", que hoy no
 *    tienen ningún banner ni copy accionable propio (ver nota en renderActiveDetail).
 */

export interface BlockadeRecord {
  active: boolean;
  reason: { name: string } | null;
}

interface Props {
  activeBlockade: { reason: string; to: string | null } | false | undefined;
  activeVacationEnd: string | null | false | undefined;
  processLimitPrefered: number | null;
  blockades: BlockadeRecord[];
  onGoToVacationsTab: () => void;
  onOpenVacationBlockInfo: () => void;
}

// Nombres tal cual los devuelve el backend (`reason.name`) — usados solo para matchear, no para
// mostrar en pantalla (el copy visible vive en BLOCK_TYPES más abajo).
const REASON_NAME = {
  tope: 'Tope de Pacientes solicitado por psicologo',
  sinHorarios: 'No cuenta con horarios de reprogramación',
  situacionPersonal: 'Situaciones personales extraordinarias',
  demasiadosPacientes: 'Demasiados pacientes',
  // Nombre real en backend/MetricsAsignacionTab.tsx: "...dentro DEL período" (no "de un período",
  // que es el label que usamos para mostrar — ver BLOCK_TYPES).
  limitePeriodo: 'Límite de pacientes dentro del período',
} as const;

type BlockKey = 'sinHorarios' | 'demasiadosPacientes' | 'limitePeriodo' | 'tope' | 'situacionPersonal' | 'vacaciones';

// Mismo copy oficial (nombre + descripción) que ya usan MetricsPsiPlusTab.tsx y
// MetricsAsignacionTab.tsx — mismo orden.
const BLOCK_TYPES: { key: BlockKey; name: string; description: string }[] = [
  {
    key: 'sinHorarios',
    name: 'No cuenta con horarios de reprogramación',
    description: 'Si necesitás habilitar más horarios en tu agenda.',
  },
  {
    key: 'demasiadosPacientes',
    name: 'Demasiados pacientes',
    description: 'Si llegaste al límite de procesos permitidos.',
  },
  {
    key: 'limitePeriodo',
    name: 'Límite de pacientes dentro de un período',
    description: 'Si se te asignaron muchos procesos en poco tiempo (específicamente 5 en un período de 30 días).',
  },
  {
    key: 'tope',
    name: 'Tope de pacientes (solicitado por psico)',
    description: 'Si vos nos pediste explícitamente atender a una menor cantidad de procesos que nuestro máximo, y llegaste a ese número.',
  },
  {
    key: 'situacionPersonal',
    name: 'Situaciones personales extraordinarias',
    description: 'Si nos comentaste que estabas atravesando una situación personal y que por eso necesitabas una pausa.',
  },
  {
    key: 'vacaciones',
    name: 'Vacaciones y días off',
    description: 'Si estás en un período importante de días off.',
  },
];

function formatShortDate(iso: string): string {
  return new Date(iso.substring(0, 10) + 'T12:00:00').toLocaleDateString('es-AR', { day: 'numeric', month: 'numeric' });
}

export const MetricsBloqueosTab: React.FC<Props> = ({
  activeBlockade,
  activeVacationEnd,
  processLimitPrefered,
  blockades,
  onGoToVacationsTab,
  onOpenVacationBlockInfo,
}) => {
  const isActive = (key: BlockKey): boolean => {
    if (key === 'vacaciones') {
      return activeVacationEnd !== undefined && activeVacationEnd !== false;
    }
    if (activeBlockade) {
      if (key === 'tope' && activeBlockade.reason === REASON_NAME.tope) return true;
      if (key === 'sinHorarios' && activeBlockade.reason === REASON_NAME.sinHorarios) return true;
      if (key === 'situacionPersonal' && activeBlockade.reason === REASON_NAME.situacionPersonal) return true;
    }
    // Demasiados pacientes / Límite de pacientes dentro de un período: no tienen banner propio,
    // se detectan directo de la lista cruda de bloqueos (misma fuente que MetricsAsignacionTab).
    if (key === 'demasiadosPacientes' || key === 'limitePeriodo') {
      const reasonName = key === 'demasiadosPacientes' ? REASON_NAME.demasiadosPacientes : REASON_NAME.limitePeriodo;
      return blockades.some((b) => b.active && b.reason?.name === reasonName);
    }
    return false;
  };

  const anyActive = BLOCK_TYPES.some((b) => isActive(b.key));

  /**
   * Texto contextual + link/botón de acción, reusando EXACTAMENTE el mismo copy que ya usa el
   * banner "Bloqueada para recibir pacientes nuevos" (PsychologistProfile.tsx) para cada caso que
   * ese banner cubre. "Demasiados pacientes" y "Límite de pacientes dentro de un período" no
   * tienen ningún banner ni copy accionable propio hoy (ver comentario en
   * MetricsAsignacionTab.tsx: "Bloqueos internos que no puede resolver la psicóloga — se ocultan
   * del mensaje accionable") — para esos dos casos no se inventa texto nuevo, solo se resalta que
   * están activos (pregunta abierta señalada en el PR).
   */
  const renderActiveDetail = (key: BlockKey): React.ReactNode => {
    switch (key) {
      case 'vacaciones': {
        const formatted = activeVacationEnd ? formatShortDate(activeVacationEnd) : null;
        return (
          <>
            {formatted
              ? <>No se te asignarán nuevos pacientes al menos hasta el <strong>{formatted}</strong>. </>
              : <>No se te asignarán nuevos pacientes por el momento. </>}
            Podés gestionar tus días desde la pestaña &quot;Vacaciones y Días off&quot;.{' '}
            <button
              type="button"
              onClick={onOpenVacationBlockInfo}
              className="underline text-orange-600 hover:text-orange-700 font-medium bg-transparent border-0 p-0 cursor-pointer"
            >
              ¿Cómo funciona el bloqueo?
            </button>
          </>
        );
      }
      case 'tope':
        return (
          <>
            No se te asignarán nuevos pacientes por el momento ya que solicitaste un tope
            {processLimitPrefered !== null ? <> de {processLimitPrefered} procesos</> : ''}.
          </>
        );
      case 'sinHorarios':
      case 'situacionPersonal': {
        const to = activeBlockade ? activeBlockade.to : null;
        const formatted = to ? formatShortDate(to) : null;
        return formatted
          ? <>No se te asignarán nuevos pacientes al menos hasta el <strong>{formatted}</strong>.</>
          : <>No se te asignarán nuevos pacientes por el momento.</>;
      }
      // Demasiados pacientes / Límite de pacientes dentro de un período: sin copy accionable
      // establecido — no se muestra texto adicional más allá de la descripción oficial de arriba.
      default:
        return null;
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <p className="text-sm text-gray-600 leading-relaxed">
        Llamamos bloqueo a la decisión de dejar de asignar nuevos consultantes durante determinada
        cantidad de tiempo. Esto no afecta de ninguna forma a los procesos que ya atendés de manera
        activa — simplemente, durante el período de bloqueo, no recibís procesos nuevos.
      </p>

      {!anyActive && (
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 sm:px-5 py-4 flex items-center gap-3">
          <div className="flex-shrink-0 w-9 h-9 rounded-xl bg-emerald-100 flex items-center justify-center">
            <CheckCircle2 className="w-5 h-5 text-emerald-600" />
          </div>
          <div>
            <p className="text-sm font-bold text-gray-800 m-0">No tenés bloqueos activos</p>
            <p className="text-xs text-gray-600 m-0 leading-snug">Podés seguir recibiendo pacientes nuevos con normalidad.</p>
          </div>
        </div>
      )}

      <div className="flex flex-col gap-3">
        {BLOCK_TYPES.map((block) => {
          const active = isActive(block.key);
          if (!active) {
            return (
              <div key={block.key} data-testid={`bloqueo-row-${block.key}`} className="rounded-xl border border-gray-100 px-4 sm:px-5 py-4">
                <p className="text-sm font-semibold text-[#4A3D73] m-0">{block.name}</p>
                <p className="text-sm text-gray-500 leading-relaxed mt-1 mb-0">{block.description}</p>
              </div>
            );
          }
          return (
            <div
              key={block.key}
              data-testid={`bloqueo-row-${block.key}`}
              className="rounded-2xl border border-orange-300 px-4 sm:px-5 py-4 flex items-start gap-3 sm:gap-4"
              style={{ background: 'rgba(255,237,213,0.5)' }}
            >
              <div className="flex-shrink-0 w-9 h-9 rounded-xl bg-orange-100 flex items-center justify-center mt-0.5">
                <Shield className="w-4 h-4 text-orange-600" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2 mb-1">
                  <p className="text-sm font-bold text-gray-800 m-0">{block.name}</p>
                  <span className="text-[11px] font-medium px-2.5 py-0.5 rounded-full bg-orange-100 text-orange-600 border border-orange-300 whitespace-nowrap">
                    Activo ahora
                  </span>
                </div>
                <p className="text-xs text-gray-600 m-0 leading-snug mb-1.5">{block.description}</p>
                {renderActiveDetail(block.key) && (
                  <p className="text-xs text-gray-700 m-0 leading-snug">{renderActiveDetail(block.key)}</p>
                )}
                {block.key === 'vacaciones' && (
                  <button
                    type="button"
                    onClick={onGoToVacationsTab}
                    className="mt-2 inline-flex items-center gap-1 border border-orange-300 rounded-full px-3.5 py-1.5 text-xs font-semibold text-orange-600 bg-white hover:bg-orange-50 transition-colors"
                  >
                    Gestionar
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default MetricsBloqueosTab;
